import { useState, useMemo } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, DollarSign, Search, Clock, Filter, Eye, Sparkles, TrendingUp, TrendingDown, Minus, ScanSearch, Check, X, HelpCircle } from 'lucide-react';
import { useWasteAnalysis, useRunWasteAnalysis, useResearchMerchants, useSetWasteStatus } from '../../hooks/useTransactions';
import type { Transaction, WastePattern } from '../../types';
import { formatTxnDate } from '../../utils/dates';
import { formatAccountName } from '../../utils/accounts';
import { cn } from '../../lib/cn';
import {
  Button,
  Card,
  DateText,
  EmptyState,
  IconButton,
  Input,
  Money,
  Select,
  Tag,
  TextArea,
  type TagTone,
} from '../ui';
import SourcePageViewer from '../SourcePageViewer';
import WasteCategorySettings from './WasteCategorySettings';

interface WasteClaimsViewProps {
  caseId: string;
  transactions: Transaction[];
  wasteTransactions: Transaction[];
  totalWasteAmount: number;
  onOverride: (t: Transaction) => void;
}

// `totalWasteAmount` stays in the props contract for callers, but the banner
// shows the confirmed-only total computed below.
export default function WasteClaimsView({
  caseId,
  transactions,
  wasteTransactions,
  onOverride,
}: WasteClaimsViewProps) {
  const { data: analysisResult } = useWasteAnalysis(caseId);
  const runAnalysis = useRunWasteAnalysis(caseId);
  const researchMerchants = useResearchMerchants(caseId);
  const setWasteStatus = useSetWasteStatus(caseId);

  const [contextText, setContextText] = useState<string | null>(null);
  const [contextExpanded, setContextExpanded] = useState(false);
  const [researchMsg, setResearchMsg] = useState<string | null>(null);

  const hasAnalysis = !!analysisResult;

  const displayContext = contextText !== null ? contextText : (analysisResult?.context_text || '');

  const suggestedTransactions = useMemo(
    () => wasteTransactions.filter((t) => t.waste_status === 'suggested'),
    [wasteTransactions]
  );
  const confirmedTransactions = useMemo(
    () => wasteTransactions.filter((t) => t.waste_status !== 'suggested'),
    [wasteTransactions]
  );
  const confirmedTotal = useMemo(
    () => confirmedTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0),
    [confirmedTransactions]
  );

  const handleResearch = () => {
    setResearchMsg(null);
    researchMerchants.mutate(undefined, {
      onSuccess: (r) => {
        const parts: string[] = [];
        parts.push(`${r.ai_lookups} new merchant${r.ai_lookups === 1 ? '' : 's'} researched`);
        if (r.cache_hits) parts.push(`${r.cache_hits} from memory`);
        if (r.flagged_confirmed) parts.push(`${r.flagged_confirmed} confirmed`);
        parts.push(`${r.flagged_suggested} suggested`);
        setResearchMsg(parts.join(' · '));
      },
      onError: () => setResearchMsg('Merchant research failed. Please try again.'),
    });
  };

  return (
    <div className="space-y-6">
      <WasteSummaryBanner
        wasteCount={confirmedTransactions.length}
        totalWasteAmount={confirmedTotal}
        hasAnalysis={hasAnalysis}
      />

      <MerchantResearchPanel
        onResearch={handleResearch}
        isRunning={researchMerchants.isPending}
        message={researchMsg}
        isError={researchMerchants.isError}
      />

      <WasteCategorySettings />

      {suggestedTransactions.length > 0 && (
        <SuggestedReviewSection
          transactions={suggestedTransactions}
          onConfirm={(id, note) => setWasteStatus.mutate({ transactionId: id, action: 'confirm', reviewNote: note })}
          onDismiss={(id, note) => setWasteStatus.mutate({ transactionId: id, action: 'dismiss', reviewNote: note })}
          isPending={setWasteStatus.isPending}
        />
      )}

      <CaseContextPanel
        contextText={displayContext}
        onContextChange={(v: string) => setContextText(v)}
        onRunAnalysis={() => {
          const ctx = displayContext.trim();
          if (ctx) runAnalysis.mutate(ctx);
        }}
        isRunning={runAnalysis.isPending}
        lastAnalysis={analysisResult}
        expanded={contextExpanded || !hasAnalysis}
        onToggleExpand={() => setContextExpanded(!contextExpanded)}
        hasAnalysis={hasAnalysis}
        error={runAnalysis.error?.message || null}
      />

      {hasAnalysis && analysisResult.patterns.length > 0 && (
        <PatternSummary
          patterns={analysisResult.patterns}
          transactions={transactions}
          onOverride={onOverride}
        />
      )}

      <FlaggedTransactionsList
        transactions={confirmedTransactions}
        allTransactions={transactions}
        onOverride={onOverride}
      />
    </div>
  );
}


function MerchantResearchPanel({
  onResearch,
  isRunning,
  message,
  isError,
}: {
  onResearch: () => void;
  isRunning: boolean;
  message: string | null;
  isError: boolean;
}) {
  return (
    <Card className="border-green-200">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2">
          <ScanSearch size={16} className="mt-1 shrink-0 text-green-700" />
          <div>
            <h3 className="t-card text-green-900">Research Merchants</h3>
            <p className="mt-1 max-w-xl text-xs text-ink-faint">
              Identify disguised venues (a casino billing as a plain name, a club as an LLC) across this case.
              Well-known waste venues are auto-flagged as likely waste for your review; uncertain ones appear below for one-click review.
              Each merchant is researched once and remembered for every future case.
            </p>
          </div>
        </div>
        <Button size="lg" icon={ScanSearch} loading={isRunning} onClick={onResearch} className="shrink-0">
          {isRunning ? 'Researching…' : 'Research merchants'}
        </Button>
      </div>
      {message && (
        <div
          className={cn(
            'mt-3 rounded-md border px-3 py-2 text-sm',
            isError ? 'border-danger bg-danger-soft text-danger' : 'border-green-200 bg-green-100 text-green-800',
          )}
        >
          {message}
        </div>
      )}
    </Card>
  );
}


function SuggestedReviewSection({
  transactions,
  onConfirm,
  onDismiss,
  isPending,
}: {
  transactions: Transaction[];
  onConfirm: (id: number, note?: string) => void;
  onDismiss: (id: number, note?: string) => void;
  isPending: boolean;
}) {
  return (
    <div className="space-y-3">
      <h3 className="t-card flex items-center gap-2 text-clay-800">
        <HelpCircle size={16} />
        Suggested — Needs Review ({transactions.length})
      </h3>
      <p className="text-xs text-ink-faint">
        The AI found these merchants but is not fully confident. Confirm to count them as waste claims, or dismiss to clear them. Your decision is final and won't be changed automatically.
      </p>
      <div className="divide-y divide-clay-100 rounded-lg border border-clay-200 bg-clay-50">
        {transactions.map((txn) => (
          <SuggestedRow
            key={txn.id}
            txn={txn}
            onConfirm={(note) => onConfirm(txn.id, note)}
            onDismiss={(note) => onDismiss(txn.id, note)}
            isPending={isPending}
          />
        ))}
      </div>
    </div>
  );
}


function SuggestedRow({
  txn,
  onConfirm,
  onDismiss,
  isPending,
}: {
  txn: Transaction;
  onConfirm: (note?: string) => void;
  onDismiss: (note?: string) => void;
  isPending: boolean;
}) {
  const [sourceOpen, setSourceOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const canViewSource = txn.statement_id && txn.source_page !== null;
  const trimmedNote = note.trim() || undefined;

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-3 text-sm">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-medium text-green-900">{txn.merchant_name || txn.description}</p>
          {typeof txn.merchant_confidence === 'number' && (
            <Tag tone="warn">{txn.merchant_confidence}% sure</Tag>
          )}
        </div>
        <div className="mt-1 flex flex-wrap gap-3 text-xs text-ink-faint">
          <span>{formatTxnDate(txn.date)}</span>
          <span>{formatAccountName(txn.bank_label)}</span>
          {txn.merchant_category && <span>{txn.merchant_category}</span>}
          <span className="max-w-72 truncate text-ink-faint">{txn.description}</span>
          {canViewSource && (
            <>
              {/* eslint-disable-next-line no-restricted-syntax -- inline citation link: no ui/LinkButton primitive exists yet */}
              <button
                onClick={(e) => { e.stopPropagation(); setSourceOpen(true); }}
                className="inline-flex items-center gap-1 text-green-700 hover:text-green-900 hover:underline"
              >
                <Eye size={12} />
                {txn.source_file} (p.{txn.source_page})
              </button>
              <SourcePageViewer
                statementId={txn.statement_id}
                pageNumber={txn.source_page!}
                sourceFile={txn.source_file}
                isOpen={sourceOpen}
                onClose={() => setSourceOpen(false)}
                highlight={txn.location}
              />
            </>
          )}
        </div>
      </div>
      <Money value={Math.abs(txn.amount)} className="shrink-0 font-semibold text-clay-800" />
      <div className="flex shrink-0 items-center gap-2">
        <Button size="sm" icon={Check} disabled={isPending} onClick={() => onConfirm(trimmedNote)}>
          Confirm
        </Button>
        <Button size="sm" variant="secondary" icon={X} disabled={isPending} onClick={() => onDismiss(trimmedNote)}>
          Dismiss
        </Button>
        {!noteOpen && (
          // eslint-disable-next-line no-restricted-syntax -- inline text link: no ui/LinkButton primitive exists yet
          <button
            onClick={() => setNoteOpen(true)}
            className="text-xs text-ink-faint underline hover:text-ink-soft"
          >
            + note
          </button>
        )}
      </div>
      {noteOpen && (
        <div className="w-full">
          <Input
            size="sm"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note for the record — e.g. why this is (or isn't) waste"
            maxLength={500}
            autoFocus
            hint="Saved with your decision and shown in the review record."
          />
        </div>
      )}
    </div>
  );
}


function WasteSummaryBanner({
  wasteCount,
  totalWasteAmount,
  hasAnalysis,
}: {
  wasteCount: number;
  totalWasteAmount: number;
  hasAnalysis: boolean;
}) {
  if (wasteCount === 0 && !hasAnalysis) {
    return (
      <Card padding="none">
        <EmptyState
          icon={Search}
          title="No waste claims yet"
          description="Enter your case context below and run the waste analysis to identify spending patterns that may constitute dissipation of community assets."
        />
      </Card>
    );
  }

  return (
    <div className="flex items-center justify-between rounded-lg border border-danger bg-danger-soft p-4">
      <div className="flex items-center gap-3">
        <DollarSign size={24} className="shrink-0 text-danger" />
        <div>
          <p className="text-sm font-medium text-danger">Potential Waste — Pending Attorney Review</p>
          <p className="text-xs text-danger">
            {wasteCount} transaction{wasteCount === 1 ? '' : 's'} flagged; confirm each before asserting as a dissipation claim
          </p>
        </div>
      </div>
      <Money value={totalWasteAmount} className="t-num-hero text-danger" />
    </div>
  );
}


function CaseContextPanel({
  contextText,
  onContextChange,
  onRunAnalysis,
  isRunning,
  lastAnalysis,
  expanded,
  onToggleExpand,
  hasAnalysis,
  error,
}: {
  contextText: string;
  onContextChange: (text: string) => void;
  onRunAnalysis: () => void;
  isRunning: boolean;
  lastAnalysis: { context_text: string | null; created_at: string | null } | null | undefined;
  expanded: boolean;
  onToggleExpand: () => void;
  hasAnalysis: boolean;
  error: string | null;
}) {
  const canRun = contextText.trim().length > 0 || (lastAnalysis?.context_text && lastAnalysis.context_text.trim().length > 0);

  return (
    <Card className="border-green-200">
      <div
        className="flex cursor-pointer items-center justify-between"
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-green-700" />
          <h3 className="t-card text-green-900">AI Waste Analysis</h3>
          {lastAnalysis?.created_at && (
            <span className="flex items-center gap-1 text-xs text-ink-faint">
              <Clock size={12} />
              Last run: {new Date(lastAnalysis.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
        </div>
        <IconButton
          icon={expanded ? ChevronDown : ChevronRight}
          size="sm"
          aria-label={expanded ? 'Collapse analysis panel' : 'Expand analysis panel'}
        />
      </div>

      {expanded && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-ink-faint">
            Describe what you know about the case — suspected spending habits, vices, concerns about the opposing party. The AI will scan all transactions for patterns matching your context.
          </p>
          <TextArea
            value={contextText}
            onChange={(e) => onContextChange(e.target.value)}
            placeholder="e.g., Husband is an alcoholic with a suspected gambling problem. Wife believes he was spending money on a girlfriend during the separation period. Large cash withdrawals are suspicious."
            rows={4}
          />
          <div className="flex items-center justify-between">
            <Button
              size="lg"
              icon={Search}
              loading={isRunning}
              disabled={!canRun}
              onClick={onRunAnalysis}
            >
              {isRunning ? 'Analyzing transactions…' : hasAnalysis ? 'Re-run Analysis' : 'Run Waste Analysis'}
            </Button>
            {isRunning && (
              <span className="text-xs text-ink-faint">This may take 15-30 seconds</span>
            )}
          </div>
          {error && (
            <div className="rounded-md border border-danger bg-danger-soft px-3 py-2 text-sm text-danger">
              Analysis failed: {error}. Please try again.
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


function PatternSummary({
  patterns,
  transactions,
  onOverride,
}: {
  patterns: WastePattern[];
  transactions: Transaction[];
  onOverride: (t: Transaction) => void;
}) {
  const [expandedPattern, setExpandedPattern] = useState<string | null>(null);

  const txnMap = useMemo(() => {
    const map = new Map<number, Transaction>();
    transactions.forEach((t) => map.set(t.id, t));
    return map;
  }, [transactions]);

  const severityColors: Record<string, { bg: string; border: string; text: string; tone: TagTone }> = {
    high: { bg: 'bg-danger-soft', border: 'border-danger', text: 'text-danger', tone: 'danger' },
    medium: { bg: 'bg-clay-50', border: 'border-clay-200', text: 'text-clay-800', tone: 'warn' },
    low: { bg: 'bg-info-soft', border: 'border-info', text: 'text-info', tone: 'info' },
  };

  return (
    <div className="space-y-3">
      <h3 className="t-card flex items-center gap-2 text-green-900">
        <AlertTriangle size={16} />
        Possible Spending Patterns ({patterns.length}) — AI-suggested, for review
      </h3>

      {patterns.map((pattern) => {
        const isExpanded = expandedPattern === pattern.category;
        const colors = severityColors[pattern.severity] || severityColors.medium;
        const patternTxns = pattern.transaction_ids
          .map((id) => txnMap.get(id))
          .filter((t): t is Transaction => !!t)
          .sort((a, b) => (a.date || '').localeCompare(b.date || ''));

        const trend = computeTrend(patternTxns);
        const TrendIcon = trend.direction === 'up' ? TrendingUp : trend.direction === 'down' ? TrendingDown : Minus;
        const trendColor = trend.direction === 'up' ? 'text-danger' : trend.direction === 'down' ? 'text-green-700' : 'text-ink-faint';

        return (
          <div key={pattern.category} className={`overflow-hidden rounded-lg border ${colors.border}`}>
            {/* eslint-disable-next-line no-restricted-syntax -- full-width disclosure row: no ui primitive fits this layout */}
            <button
              className={`w-full ${colors.bg} flex items-center justify-between px-4 py-3 text-left`}
              onClick={() => setExpandedPattern(isExpanded ? null : pattern.category)}
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <Tag tone={colors.tone}>{pattern.severity}</Tag>
                <div className="min-w-0">
                  <p className={`text-sm font-semibold ${colors.text}`}>{pattern.category}</p>
                  <p className="truncate text-xs text-ink-faint">{pattern.description}</p>
                </div>
              </div>
              <div className="ml-4 flex shrink-0 items-center gap-4">
                <div className="flex items-center gap-1" title={`Trend: ${trend.label}`}>
                  <TrendIcon size={16} className={trendColor} />
                  <span className={`text-xs ${trendColor}`}>{trend.label}</span>
                </div>
                <div className="text-right">
                  <Money value={pattern.total_amount} className="text-sm font-bold text-danger" />
                  <p className="text-xs text-ink-faint">
                    {pattern.transaction_count} transaction{pattern.transaction_count === 1 ? '' : 's'}
                    {pattern.date_range && (
                      <span className="ml-1">
                        ({formatDateShort(pattern.date_range.start)} – {formatDateShort(pattern.date_range.end)})
                      </span>
                    )}
                  </p>
                </div>
                {isExpanded ? <ChevronDown size={16} className="text-ink-faint" /> : <ChevronRight size={16} className="text-ink-faint" />}
              </div>
            </button>

            {isExpanded && (
              <div className="divide-y divide-cream-dark bg-cream-light">
                {patternTxns.map((txn) => (
                  <PatternTransactionRow key={txn.id} txn={txn} onOverride={onOverride} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function PatternTransactionRow({
  txn,
  onOverride,
}: {
  txn: Transaction;
  onOverride: (t: Transaction) => void;
}) {
  const [sourceOpen, setSourceOpen] = useState(false);
  const canViewSource = txn.statement_id && txn.source_page !== null;

  return (
    <div className="flex items-center justify-between px-4 py-2 text-sm hover:bg-cream">
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-green-900">{txn.description}</p>
        <div className="mt-1 flex gap-3 text-xs text-ink-faint">
          <span>{formatTxnDate(txn.date)}</span>
          <span>{formatAccountName(txn.bank_label)}</span>
          {txn.merchant_category && <span>{txn.merchant_category}</span>}
          {canViewSource && (
            <>
              {/* eslint-disable-next-line no-restricted-syntax -- inline citation link: no ui/LinkButton primitive exists yet */}
              <button
                onClick={(e) => { e.stopPropagation(); setSourceOpen(true); }}
                className="inline-flex items-center gap-1 text-green-700 hover:text-green-900 hover:underline"
              >
                <Eye size={12} />
                {txn.source_file} (p.{txn.source_page})
              </button>
              <SourcePageViewer
                statementId={txn.statement_id}
                pageNumber={txn.source_page!}
                sourceFile={txn.source_file}
                isOpen={sourceOpen}
                onClose={() => setSourceOpen(false)}
                highlight={txn.location}
              />
            </>
          )}
        </div>
      </div>
      <div className="ml-4 flex shrink-0 items-center gap-3">
        <Money value={Math.abs(txn.amount)} className="font-semibold text-danger" />
        {/* eslint-disable-next-line no-restricted-syntax -- inline text link: no ui/LinkButton primitive exists yet */}
        <button
          onClick={() => onOverride(txn)}
          className="text-xs text-green-700 underline hover:text-green-900"
        >
          Override
        </button>
      </div>
    </div>
  );
}


// `allTransactions` stays in the props contract for the caller; this list only
// renders the flagged subset.
function FlaggedTransactionsList({
  transactions,
  onOverride,
}: {
  transactions: Transaction[];
  allTransactions: Transaction[];
  onOverride: (t: Transaction) => void;
}) {
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [accountFilter, setAccountFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [sortField, setSortField] = useState<'date' | 'amount' | 'severity'>('amount');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const categories = useMemo(() => {
    const cats = new Set<string>();
    transactions.forEach((t) => {
      if (t.merchant_category) cats.add(t.merchant_category);
      if (t.waste_category) cats.add(t.waste_category);
    });
    return Array.from(cats).sort();
  }, [transactions]);

  const accounts = useMemo(() => {
    const accts = new Set<string>();
    transactions.forEach((t) => {
      if (t.bank_label) accts.add(t.bank_label);
    });
    return Array.from(accts).sort();
  }, [transactions]);

  const filtered = useMemo(() => {
    let result = [...transactions];

    if (severityFilter !== 'all') {
      result = result.filter((t) => t.waste_severity === severityFilter);
    }
    if (categoryFilter !== 'all') {
      result = result.filter((t) => t.merchant_category === categoryFilter || t.waste_category === categoryFilter);
    }
    if (accountFilter !== 'all') {
      result = result.filter((t) => t.bank_label === accountFilter);
    }
    if (dateFrom) {
      result = result.filter((t) => (t.date || '') >= dateFrom);
    }
    if (dateTo) {
      result = result.filter((t) => (t.date || '') <= dateTo);
    }

    const severityRank: Record<string, number> = { high: 3, medium: 2, low: 1 };
    result.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'date') {
        cmp = (a.date || '').localeCompare(b.date || '');
      } else if (sortField === 'amount') {
        cmp = Math.abs(a.amount) - Math.abs(b.amount);
      } else if (sortField === 'severity') {
        cmp = (severityRank[a.waste_severity || 'low'] || 0) - (severityRank[b.waste_severity || 'low'] || 0);
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [transactions, severityFilter, categoryFilter, accountFilter, dateFrom, dateTo, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice(page * pageSize, (page + 1) * pageSize);
  const filteredTotal = filtered.reduce((sum, t) => sum + Math.abs(t.amount), 0);

  const handleSort = (field: 'date' | 'amount' | 'severity') => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(0);
  };

  if (transactions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="t-card flex items-center gap-2 text-green-900">
          <Filter size={16} />
          All Flagged Transactions ({filtered.length})
          {filtered.length !== transactions.length && (
            <span className="text-xs font-normal text-ink-faint">
              of {transactions.length} total
            </span>
          )}
        </h3>
        <Money value={filteredTotal} className="text-sm font-semibold text-danger" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          size="sm"
          aria-label="Filter by severity"
          value={severityFilter}
          onChange={(e) => { setSeverityFilter(e.target.value); setPage(0); }}
          options={[
            { value: 'all', label: 'All Severities' },
            { value: 'high', label: 'High' },
            { value: 'medium', label: 'Medium' },
            { value: 'low', label: 'Low' },
          ]}
        />
        <Select
          size="sm"
          aria-label="Filter by category"
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
          options={[
            { value: 'all', label: 'All Categories' },
            ...categories.map((c) => ({ value: c, label: c })),
          ]}
        />
        <Select
          size="sm"
          aria-label="Filter by account"
          value={accountFilter}
          onChange={(e) => { setAccountFilter(e.target.value); setPage(0); }}
          options={[
            { value: 'all', label: 'All Accounts' },
            ...accounts.map((a) => ({ value: a, label: formatAccountName(a) })),
          ]}
        />
        <div className="flex items-center gap-1">
          <span className="text-xs text-ink-faint">From</span>
          <Input
            size="sm"
            type="date"
            aria-label="From date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            className="w-36"
          />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-ink-faint">To</span>
          <Input
            size="sm"
            type="date"
            aria-label="To date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            className="w-36"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-surface-card">
        {/* eslint-disable-next-line no-restricted-syntax -- citation buttons + tags per cell and 3-field sort: restyled in place, DataTable cannot host these */}
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th
                className="t-eyebrow cursor-pointer select-none px-3 py-2 text-left hover:text-ink"
                aria-sort={sortField === 'date' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                onClick={() => handleSort('date')}
              >
                Date {sortField === 'date' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>
              <th className="t-eyebrow px-3 py-2 text-left">Description</th>
              <th className="t-eyebrow px-3 py-2 text-left">Account</th>
              <th className="t-eyebrow px-3 py-2 text-left">Category</th>
              <th
                className="t-eyebrow cursor-pointer select-none px-3 py-2 text-left hover:text-ink"
                aria-sort={sortField === 'severity' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                onClick={() => handleSort('severity')}
              >
                Severity {sortField === 'severity' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>
              <th
                className="t-eyebrow cursor-pointer select-none px-3 py-2 text-right hover:text-ink"
                aria-sort={sortField === 'amount' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                onClick={() => handleSort('amount')}
              >
                Amount {sortField === 'amount' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>
              <th className="t-eyebrow px-3 py-2 text-right">Source</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {paged.map((txn) => (
              <FlaggedTransactionRow key={txn.id} txn={txn} onOverride={onOverride} />
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-ink-faint">
          <span>
            Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, filtered.length)} of {filtered.length}
          </span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function FlaggedTransactionRow({
  txn,
  onOverride,
}: {
  txn: Transaction;
  onOverride: (t: Transaction) => void;
}) {
  const [sourceOpen, setSourceOpen] = useState(false);
  const canViewSource = txn.statement_id && txn.source_page !== null;

  const severityTone: Record<string, TagTone> = {
    high: 'danger',
    medium: 'warn',
    low: 'info',
  };

  return (
    <tr className="h-11 hover:bg-cream">
      <td className="whitespace-nowrap px-3 py-2 text-xs text-ink-faint">
        <DateText value={txn.date} />
      </td>
      <td className="px-3 py-2">
        <p className="max-w-64 truncate text-sm font-medium text-green-900">{txn.description}</p>
        {txn.waste_category && (
          <p className="max-w-64 truncate text-xs text-ink-faint">{txn.waste_category}</p>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-xs text-ink-faint">{formatAccountName(txn.bank_label)}</td>
      <td className="whitespace-nowrap px-3 py-2 text-xs text-ink-faint">{txn.merchant_category || '—'}</td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          {txn.waste_severity && (
            <Tag tone={severityTone[txn.waste_severity] || 'info'}>
              {txn.waste_severity}
            </Tag>
          )}
          {txn.waste_source === 'ai_merchant' && (
            <Tag tone="good" size="sm" icon={Sparkles} title="Identified by AI merchant research">
              AI
            </Tag>
          )}
        </div>
        {txn.waste_reviewed_at && (
          <p
            className="mt-1 whitespace-nowrap text-2xs text-ink-faint"
            title={[
              txn.waste_reviewed_by && txn.waste_reviewed_by.includes('@')
                ? `Reviewed by ${txn.waste_reviewed_by}`
                : 'Reviewed',
              txn.waste_review_note ? `Note: ${txn.waste_review_note}` : null,
            ].filter(Boolean).join(' — ')}
          >
            ✓ Reviewed {new Date(txn.waste_reviewed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right">
        <Money value={Math.abs(txn.amount)} className="font-semibold text-danger" />
      </td>
      <td className="px-3 py-2 text-right">
        {canViewSource && (
          <>
            {/* eslint-disable-next-line no-restricted-syntax -- inline citation link: no ui/LinkButton primitive exists yet */}
            <button
              onClick={(e) => { e.stopPropagation(); setSourceOpen(true); }}
              className="inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-900 hover:underline"
            >
              <Eye size={12} />
              p.{txn.source_page}
            </button>
            <SourcePageViewer
              statementId={txn.statement_id}
              pageNumber={txn.source_page!}
              sourceFile={txn.source_file}
              isOpen={sourceOpen}
              onClose={() => setSourceOpen(false)}
            />
          </>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {/* eslint-disable-next-line no-restricted-syntax -- inline text link: no ui/LinkButton primitive exists yet */}
        <button
          onClick={() => onOverride(txn)}
          className="text-xs text-green-700 underline hover:text-green-900"
        >
          Override
        </button>
      </td>
    </tr>
  );
}


function computeTrend(txns: Transaction[]): { label: string; direction: 'up' | 'down' | 'stable' } {
  if (txns.length < 2) return { label: 'Too few transactions', direction: 'stable' };
  const sorted = [...txns].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const mid = Math.floor(sorted.length / 2);
  const firstHalf = sorted.slice(0, mid);
  const secondHalf = sorted.slice(mid);
  const firstTotal = firstHalf.reduce((s, t) => s + Math.abs(t.amount), 0);
  const secondTotal = secondHalf.reduce((s, t) => s + Math.abs(t.amount), 0);
  if (secondTotal > firstTotal * 1.15) return { label: 'Increasing', direction: 'up' };
  if (secondTotal < firstTotal * 0.85) return { label: 'Decreasing', direction: 'down' };
  return { label: 'Stable', direction: 'stable' };
}


function formatDateShort(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  } catch {
    return dateStr;
  }
}
